import React, { useMemo, useState } from "react";
import "../../components/EventUserDashboard.css";

interface TicketStatusDto {
    ticketId: number;
    ticketCode: string;
    collectionName: string;
    ticketNumber: number;
    bar: number;
    maxDrinks: number;
    usedDrinks: number;
    remainingDrinks: number;
}

enum DrinkType {
    BEER = 1,
    WATER = 2,
    WINE = 3
}

enum Bar {
    BAR1 = 1,
    BAR2 = 2,
    BAR3 = 3
}

const getBarName = (bar: number) => {
    switch (bar) {
        case Bar.BAR1:
            return "VIP Lounge";
        case Bar.BAR2:
            return "Hygge Lounge";
        case Bar.BAR3:
            return "Jail House Bar";
        default:
            return `Bar ${bar}`;
    }
};

const toNumber = (value: any, fallback = 0): number => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const DrinkTicketOrder: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    const [eventId, setEventId] = useState<number>(0);
    const [ticketCode, setTicketCode] = useState("");
    const [ticketStatus, setTicketStatus] = useState<TicketStatusDto | null>(null);

    const [beerCount, setBeerCount] = useState(0);
    const [waterCount, setWaterCount] = useState(0);
    const [wineCount, setWineCount] = useState(0);

    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmScreen, setShowConfirmScreen] = useState(false);

    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const totalInCart = beerCount + waterCount + wineCount;
    const remaining = toNumber(ticketStatus?.remainingDrinks, 0);
    const canAddMore = totalInCart < remaining;

    const resetOrderCounts = () => {
        setBeerCount(0);
        setWaterCount(0);
        setWineCount(0);
    };

    const lookupTicket = async () => {
        if (!eventId || !ticketCode.trim()) {
            setError("Skriv event og billetkode");
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setSuccess(null);
            setShowConfirmScreen(false);

            const res = await fetch(
                `${apiUrl}/api/drink-tickets/status?ticketCode=${encodeURIComponent(ticketCode.trim())}&eventId=${eventId}`
            );

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || "Kunne ikke finde billet");
            }

            const result = await res.json();
            setTicketStatus(result);
            resetOrderCounts();
        } catch (err: any) {
            console.error(err);
            setTicketStatus(null);
            setError(err?.message || "Kunne ikke finde billet");
        } finally {
            setLoading(false);
        }
    };

    const addDrink = (type: DrinkType) => {
        if (!ticketStatus || !canAddMore) return;

        setSuccess(null);
        setError(null);

        if (type === DrinkType.BEER) setBeerCount((b) => b + 1);
        if (type === DrinkType.WATER) setWaterCount((w) => w + 1);
        if (type === DrinkType.WINE) setWineCount((w) => w + 1);
    };

    const removeDrink = (type: DrinkType) => {
        setSuccess(null);
        setError(null);

        if (type === DrinkType.BEER) setBeerCount((b) => Math.max(0, b - 1));
        if (type === DrinkType.WATER) setWaterCount((w) => Math.max(0, w - 1));
        if (type === DrinkType.WINE) setWineCount((w) => Math.max(0, w - 1));
    };

    const openConfirmScreen = () => {
        if (!ticketStatus || totalInCart <= 0) return;

        if (totalInCart > remaining) {
            setError("Du kan ikke bestille flere drinks end der er tilbage på billetten.");
            return;
        }

        setError(null);
        setSuccess(null);
        setShowConfirmScreen(true);
    };

    const confirmDelivery = async () => {
        if (!ticketStatus || !ticketCode.trim() || totalInCart <= 0 || isSubmitting) return;

        if (totalInCart > remaining) {
            setError("Du kan ikke bestille flere drinks end der er tilbage på billetten.");
            setShowConfirmScreen(false);
            return;
        }

        try {
            setIsSubmitting(true);
            setError(null);
            setSuccess(null);

            const orders: DrinkType[] = [
                ...Array(beerCount).fill(DrinkType.BEER),
                ...Array(waterCount).fill(DrinkType.WATER),
                ...Array(wineCount).fill(DrinkType.WINE),
            ];

            for (const drinkType of orders) {
                const res = await fetch(`${apiUrl}/api/drink-tickets/order`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ticketCode: ticketCode.trim(),
                        eventId,
                        drinkType
                    })
                });

                if (!res.ok) {
                    const text = await res.text();
                    throw new Error(text || `Bestilling fejlede (${res.status})`);
                }
            }

            setSuccess("Drinks er udleveret");
            setShowConfirmScreen(false);
            resetOrderCounts();
            await lookupTicket();
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Der opstod en fejl ved udlevering");
            setShowConfirmScreen(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const titleText = useMemo(() => {
        if (!ticketStatus) return "Bestil på billet";
        return `${ticketStatus.collectionName} #${ticketStatus.ticketNumber}`;
    }, [ticketStatus]);

    return (
        <div className="dashContainer">
            <div className="wrapper">
                <div className="top">
                    <div className="name">{titleText}</div>
                    <div className="meta">
                        {ticketStatus
                            ? `Bestil i ${getBarName(ticketStatus.bar)}`
                            : "Slå billet op og bestil drinks"}
                    </div>
                </div>

                <div className="content">
                    {loading && <div style={{ marginBottom: 16 }}>Henter billet...</div>}

                    {success && (
                        <div className="statusBadge" style={{ padding: 8, marginBottom: 16 }}>
                            {success}
                        </div>
                    )}

                    {error && (
                        <div className="badgeRed" style={{ padding: 8, marginBottom: 16 }}>
                            {error}
                        </div>
                    )}

                    <div className="sectionTitle">Find billet</div>

                    <div className="statsGrid3" style={{ marginTop: 12 }}>
                        <div className="stat" style={{ padding: 16 }}>
                            <label>Event ID</label>
                            <input
                                type="number"
                                min={1}
                                value={eventId || ""}
                                onChange={(e) => setEventId(toNumber(e.target.value, 0))}
                            />
                        </div>

                        <div className="stat" style={{ padding: 16 }}>
                            <label>Billetkode</label>
                            <input
                                type="text"
                                value={ticketCode}
                                onChange={(e) => setTicketCode(e.target.value)}
                                placeholder="Fx SED-3-12"
                            />
                        </div>

                        <div className="stat" style={{ padding: 16, display: "flex", alignItems: "end" }}>
                            <button type="button" style={{ width: "100%" }} onClick={lookupTicket}>
                                Find billet
                            </button>
                        </div>
                    </div>

                    {ticketStatus && !showConfirmScreen && (
                        <>
                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Billetstatus
                            </div>

                            <div className="statsGrid3" style={{ marginTop: 12 }}>
                                <div className="stat" style={{ padding: 16 }}>
                                    <div style={{ fontWeight: 800 }}>Collection</div>
                                    <div style={{ marginTop: 8 }}>{ticketStatus.collectionName}</div>
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <div style={{ fontWeight: 800 }}>Bar</div>
                                    <div style={{ marginTop: 8 }}>{getBarName(ticketStatus.bar)}</div>
                                </div>

                                <div className="stat" style={{ padding: 16 }}>
                                    <div style={{ fontWeight: 800 }}>Tilbage</div>
                                    <div style={{ marginTop: 8 }}>{ticketStatus.remainingDrinks}</div>
                                </div>
                            </div>

                            <div className="sectionTitle" style={{ marginTop: 30 }}>
                                Vælg drinks
                            </div>

                            <div
                                className="stat"
                                style={{
                                    padding: 16,
                                    marginTop: 16,
                                    display: "grid",
                                    gap: 12,
                                }}
                            >
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>🍺 Øl</span>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <button type="button" onClick={() => removeDrink(DrinkType.BEER)}>-</button>
                                        <strong>{beerCount}</strong>
                                        <button type="button" onClick={() => addDrink(DrinkType.BEER)} disabled={!canAddMore}>+</button>
                                    </div>
                                </div>

                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>🥤 Vand</span>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <button type="button" onClick={() => removeDrink(DrinkType.WATER)}>-</button>
                                        <strong>{waterCount}</strong>
                                        <button type="button" onClick={() => addDrink(DrinkType.WATER)} disabled={!canAddMore}>+</button>
                                    </div>
                                </div>

                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <span>🍷 Vin</span>
                                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                        <button type="button" onClick={() => removeDrink(DrinkType.WINE)}>-</button>
                                        <strong>{wineCount}</strong>
                                        <button type="button" onClick={() => addDrink(DrinkType.WINE)} disabled={!canAddMore}>+</button>
                                    </div>
                                </div>
                            </div>

                            <div className="stat" style={{ padding: 16, marginTop: 20 }}>
                                <div>🍺 {beerCount}</div>
                                <div>🥤 {waterCount}</div>
                                <div>🍷 {wineCount}</div>
                                <div style={{ marginTop: 10, fontWeight: 800 }}>Total: {totalInCart}</div>

                                <button
                                    type="button"
                                    style={{
                                        marginTop: 15,
                                        width: "100%",
                                        opacity: totalInCart <= 0 ? 0.6 : 1,
                                        cursor: totalInCart <= 0 ? "not-allowed" : "pointer",
                                    }}
                                    onClick={openConfirmScreen}
                                    disabled={totalInCart <= 0}
                                >
                                    Bestil
                                </button>
                            </div>
                        </>
                    )}

                    {ticketStatus && showConfirmScreen && (
                        <div
                            className="stat"
                            style={{
                                padding: 30,
                                marginTop: 20,
                                minHeight: 420,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                            }}
                        >
                            <div style={{ fontSize: 34, fontWeight: 800 }}>
                                {ticketStatus.collectionName} #{ticketStatus.ticketNumber}
                            </div>

                            <div style={{ fontSize: 24, marginTop: 12 }}>
                                {getBarName(ticketStatus.bar)}
                            </div>

                            <div style={{ fontSize: 24, marginTop: 20 }}>Udlever følgende:</div>

                            <div style={{ fontSize: 40, fontWeight: 800, marginTop: 30, lineHeight: 1.6 }}>
                                {beerCount > 0 && <div>🍺 {beerCount} Øl</div>}
                                {waterCount > 0 && <div>🥤 {waterCount} Vand</div>}
                                {wineCount > 0 && <div>🍷 {wineCount} Vin</div>}
                            </div>

                            <div style={{ fontSize: 28, fontWeight: 800, marginTop: 25 }}>
                                Total: {totalInCart}
                            </div>

                            <div style={{ display: "flex", gap: 12, width: "100%", marginTop: 30 }}>
                                <button
                                    type="button"
                                    style={{ flex: 1, padding: 16 }}
                                    onClick={() => setShowConfirmScreen(false)}
                                    disabled={isSubmitting}
                                >
                                    Tilbage
                                </button>

                                <button
                                    type="button"
                                    style={{ flex: 1, padding: 16, fontWeight: 800 }}
                                    onClick={confirmDelivery}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "Gemmer..." : "Udlever"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DrinkTicketOrder;